import { router, useForm, usePage } from "@inertiajs/react"
import {
  Building2Icon,
  CheckCircle2Icon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardCopyIcon,
  ClipboardListIcon,
  DownloadIcon,
  ExternalLinkIcon,
  GlobeIcon,
  LayoutDashboardIcon,
  MailIcon,
  MapPinIcon,
  PaletteIcon,
  RocketIcon,
  SparklesIcon,
  UserIcon,
  WifiIcon,
} from "lucide-react"
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react"
import QRCode from "react-qr-code"

import {
  CHECKLIST_LABELS,
  ChecklistItem,
  ChecklistProgressBar,
  checklistProgress,
  type ChecklistLinks,
} from "@/components/shared/checklist"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { FieldError } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { OnboardCompletedProperty, OnboardingStatus, WizardRegion } from "@/lib/types"
import type { SharedProps } from "@/types"

// ---- Types ----

interface WizardFormData {
  // Step 1
  name: string
  property_type: string
  owner_email: string
  owner_first_name: string
  owner_last_name: string
  region_id: number | ""
  address: string
  contact_phone: string
  contact_email: string
  // Step 2
  logo_image: File | null
  cover_image: File | null
  primary_color: string
  welcome_message: string
  slug: string
  wifi_integration_type: string
  wifi_network_name: string
  wifi_password: string
  // Step 3
  create_inside_info: boolean
  create_default_guide: boolean
  create_qr_share: boolean
  create_wifi_share: boolean
  send_welcome_email: boolean
}

interface Props {
  regions: WizardRegion[]
  completed_property: OnboardCompletedProperty | null
  // `submit` is present in the form flow; the completion flow supplies the rest.
  urls: {
    submit?: string
    show?: string
    onboard?: string
    dashboard?: string
    settings_branding?: string
    settings_wifi?: string
    inside?: string
    guides?: string
    delivery?: string
    pois?: string
    download_qr_base?: string
  }
}

type CompletionUrls = Props["urls"]

const PROPERTY_TYPES = [
  { value: "hotel",     label: "Hotel" },
  { value: "bnb",       label: "B&B" },
  { value: "apartment", label: "Apartment" },
  { value: "hostel",    label: "Hostel" },
  { value: "villa",     label: "Villa" },
  { value: "other",     label: "Other" },
]

const WIFI_INTEGRATION_TYPES = [
  { value: "qr_only",             label: "QR only (no WiFi redirect)" },
  { value: "post_login_redirect", label: "Post-login redirect" },
  { value: "captive_portal",      label: "Captive portal" },
]

const ONBOARDING_STATUS_LABELS: Record<string, string> = {
  pending:          "Pending",
  materials_sent:   "Materials sent",
  wifi_configured:  "WiFi configured",
  live:             "Live",
}

// Color-coded onboarding status (DESIGN.md status badge colors).
const ONBOARDING_STATUS_BADGE_CLASSES: Record<OnboardingStatus, string> = {
  pending:         "border-yellow-300 bg-yellow-50 text-yellow-700",
  materials_sent:  "border-blue-300 bg-blue-50 text-blue-700",
  wifi_configured: "border-purple-300 bg-purple-50 text-purple-700",
  live:            "border-green-300 bg-green-50 text-green-700",
}

const STEP_META = [
  { number: 1, title: "Basics",               description: "Property details and owner" },
  { number: 2, title: "Customise & delivery", description: "Appearance and WiFi setup" },
  { number: 3, title: "What gets created",    description: "Review & confirm" },
] as const

// ---- Helpers ----

function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
}

// ---- Sub-components ----

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <nav aria-label={`Step ${current} of ${total}`} className="mb-8">
      <ol className="flex items-center">
        {STEP_META.map((step, i) => (
          <li key={step.number} className="flex items-center">
            <div className="flex items-center gap-3">
              {/* Step circle */}
              <div
                className={[
                  "flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-all",
                  step.number < current
                    ? "bg-primary text-primary-foreground"
                    : step.number === current
                      ? "bg-primary text-primary-foreground ring-2 ring-primary/30 ring-offset-2 ring-offset-background"
                      : "border-2 border-muted-foreground/25 text-muted-foreground",
                ].join(" ")}
                aria-current={step.number === current ? "step" : undefined}
              >
                {step.number < current ? (
                  <CheckIcon className="size-4" />
                ) : (
                  step.number
                )}
              </div>
              {/* Label -- hidden on mobile, visible from sm up */}
              <div className="hidden sm:block">
                <p className={[
                  "text-sm font-medium leading-none",
                  step.number === current ? "text-foreground" : "text-muted-foreground",
                ].join(" ")}>
                  {step.title}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {step.description}
                </p>
              </div>
            </div>
            {/* Connector line */}
            {i < total - 1 && (
              <div
                className={[
                  "mx-3 h-px w-8 sm:w-12",
                  step.number < current ? "bg-primary" : "bg-border",
                ].join(" ")}
                aria-hidden="true"
              />
            )}
          </li>
        ))}
      </ol>
      {/* Mobile-only step label */}
      <p className="mt-3 text-sm font-medium text-muted-foreground sm:hidden" aria-hidden="true">
        Step {current}: {STEP_META[current - 1].title}
      </p>
    </nav>
  )
}

// ---- Field group helper ----

function FieldGroup({
  label,
  icon: Icon,
  children,
}: {
  label: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Icon className="size-4" />
        <span>{label}</span>
      </div>
      {children}
    </div>
  )
}

// ---- Step 1 ----

interface Step1Props {
  data: WizardFormData
  setData: <K extends keyof WizardFormData>(key: K, value: WizardFormData[K]) => void
  regions: WizardRegion[]
  errors: Record<string, string>
  guestBaseUrl: string
}

function Step1Basics({ data, setData, regions, errors, guestBaseUrl }: Step1Props) {
  const nameRef = useRef<HTMLInputElement>(null)
  const [showSlugEdit, setShowSlugEdit] = useState(false)

  useEffect(() => {
    nameRef.current?.focus()
  }, [])

  function handleNameChange(name: string) {
    setData("name", name)
    if (!data.slug || data.slug === slugify(data.name)) {
      setData("slug", slugify(name))
    }
  }

  return (
    <div className="space-y-6">
      {/* Property identity */}
      <FieldGroup label="Property identity" icon={Building2Icon}>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="name">Property name <span className="text-destructive">*</span></Label>
            <Input
              ref={nameRef}
              id="name"
              value={data.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Casa do Bairro"
              autoComplete="organization"
            />
            {/* Slug preview — only shown once the name has a value */}
            {data.name && (
              <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                <span>{guestBaseUrl.replace(/^https?:\/\//, "")}/{data.slug}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-xs underline"
                  onClick={() => setShowSlugEdit(true)}
                >
                  change URL
                </Button>
              </div>
            )}
            {showSlugEdit && (
              <div className="mt-2 grid gap-1.5">
                <Label htmlFor="slug">URL slug</Label>
                <Input
                  id="slug"
                  value={data.slug}
                  onChange={(e) => setData("slug", e.target.value)}
                  placeholder="casa-do-bairro"
                />
                <FieldError>{errors.slug}</FieldError>
              </div>
            )}
            <FieldError>{errors.name}</FieldError>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="property_type">Property type <span className="text-destructive">*</span></Label>
              <Select value={data.property_type} onValueChange={(v) => setData("property_type", v)}>
                <SelectTrigger id="property_type">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError>{errors.property_type}</FieldError>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="region_id">Region <span className="text-destructive">*</span></Label>
              <Select
                value={data.region_id === "" ? "" : String(data.region_id)}
                onValueChange={(v) => setData("region_id", Number(v))}
              >
                <SelectTrigger id="region_id">
                  <SelectValue placeholder="Select region..." />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError>{errors.region_id}</FieldError>
            </div>
          </div>
        </div>
      </FieldGroup>

      <Separator />

      {/* Owner */}
      <FieldGroup label="Owner" icon={UserIcon}>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="owner_email">Owner email <span className="text-destructive">*</span></Label>
            <Input
              id="owner_email"
              type="email"
              value={data.owner_email}
              onChange={(e) => setData("owner_email", e.target.value)}
              placeholder="owner@example.com"
              autoComplete="email"
            />
            <p className="text-xs text-muted-foreground">
              An existing account will be linked automatically. New users receive a setup email.
            </p>
            <FieldError>{errors.owner_email}</FieldError>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="owner_first_name">First name</Label>
              <Input
                id="owner_first_name"
                value={data.owner_first_name}
                onChange={(e) => setData("owner_first_name", e.target.value)}
                placeholder="Maria"
                autoComplete="given-name"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="owner_last_name">Last name</Label>
              <Input
                id="owner_last_name"
                value={data.owner_last_name}
                onChange={(e) => setData("owner_last_name", e.target.value)}
                placeholder="Santos"
                autoComplete="family-name"
              />
            </div>
          </div>
        </div>
      </FieldGroup>

      <Separator />

      {/* Location */}
      <FieldGroup label="Location" icon={MapPinIcon}>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              value={data.address}
              onChange={(e) => setData("address", e.target.value)}
              placeholder="Rua Augusta 24, Lisbon, Portugal"
              autoComplete="street-address"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="contact_phone">Contact phone</Label>
              <Input
                id="contact_phone"
                type="tel"
                value={data.contact_phone}
                onChange={(e) => setData("contact_phone", e.target.value)}
                placeholder="+351 21 000 0000"
                autoComplete="tel"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="contact_email">Contact email</Label>
              <Input
                id="contact_email"
                type="email"
                value={data.contact_email}
                onChange={(e) => setData("contact_email", e.target.value)}
                placeholder="info@property.com"
                autoComplete="email"
              />
            </div>
          </div>
        </div>
      </FieldGroup>
    </div>
  )
}

// ---- Step 2 ----

interface Step2Props {
  data: WizardFormData
  setData: <K extends keyof WizardFormData>(key: K, value: WizardFormData[K]) => void
}

function Step2Branding({ data, setData }: Step2Props) {
  return (
    <div className="space-y-6">
      {/* WiFi first — hotels care more about WiFi redirect than branding */}
      <FieldGroup label="WiFi integration" icon={WifiIcon}>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="wifi_integration_type">Integration type</Label>
            <Select
              value={data.wifi_integration_type}
              onValueChange={(v) => {
                setData("wifi_integration_type", v)
                if (v === "qr_only") setData("create_wifi_share", false as WizardFormData["create_wifi_share"])
              }}
            >
              <SelectTrigger id="wifi_integration_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WIFI_INTEGRATION_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {data.wifi_integration_type === "qr_only"
                ? "Guests access the guide via QR code only. WiFi can be configured later."
                : data.wifi_integration_type === "post_login_redirect"
                  ? "Guests are redirected to the guide after connecting to WiFi."
                  : "The guide is shown as the WiFi captive portal login page."}
            </p>
          </div>

          {data.wifi_integration_type !== "qr_only" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="wifi_network_name">Network name (SSID)</Label>
                <Input
                  id="wifi_network_name"
                  value={data.wifi_network_name}
                  onChange={(e) => setData("wifi_network_name", e.target.value)}
                  placeholder="Guest_WiFi"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="wifi_password">WiFi password</Label>
                <Input
                  id="wifi_password"
                  type="password"
                  value={data.wifi_password}
                  onChange={(e) => setData("wifi_password", e.target.value)}
                  placeholder="Enter password"
                  autoComplete="off"
                />
              </div>
            </div>
          )}
        </div>
      </FieldGroup>

      <Separator />

      {/* Branding */}
      <FieldGroup label="Branding" icon={PaletteIcon}>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="logo_image">Logo</Label>
            {data.logo_image && (
              <img
                src={URL.createObjectURL(data.logo_image)}
                alt="Logo preview"
                className="h-16 rounded-lg object-contain border"
              />
            )}
            <Input
              id="logo_image"
              type="file"
              accept="image/*"
              onChange={(e) => setData("logo_image", e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-muted-foreground">
              Displayed on the guest guide. Recommended: square image, at least 200×200px.
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="cover_image">Cover photo</Label>
            {data.cover_image && (
              <img
                src={URL.createObjectURL(data.cover_image)}
                alt="Cover preview"
                className="h-32 w-full rounded-lg object-cover border"
              />
            )}
            <Input
              id="cover_image"
              type="file"
              accept="image/*"
              onChange={(e) => setData("cover_image", e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-muted-foreground">
              Shown on the WiFi captive portal. Recommended: 1260×750px.
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="primary_color">Brand color</Label>
            <div className="flex items-center gap-3">
              <label
                htmlFor="primary_color"
                className="relative flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border shadow-sm transition-shadow hover:shadow-md"
                style={{ backgroundColor: data.primary_color || "#ffffff" }}
              >
                <input
                  id="primary_color"
                  type="color"
                  value={data.primary_color || "#000000"}
                  onChange={(e) => setData("primary_color", e.target.value)}
                  className="absolute inset-0 cursor-pointer opacity-0"
                  aria-label="Pick a brand color"
                />
              </label>
              <Input
                value={data.primary_color}
                onChange={(e) => setData("primary_color", e.target.value)}
                placeholder="#3b82f6"
                className="max-w-32 font-mono text-sm"
                aria-label="Brand color hex value"
              />
              {data.primary_color && (
                <span className="text-xs text-muted-foreground">
                  Preview
                  <span
                    className="ml-1.5 inline-block h-3 w-6 rounded-sm border align-middle"
                    style={{ backgroundColor: data.primary_color }}
                    aria-hidden="true"
                  />
                </span>
              )}
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="welcome_message">Welcome message</Label>
            <Textarea
              id="welcome_message"
              value={data.welcome_message}
              onChange={(e) => setData("welcome_message", e.target.value)}
              rows={3}
              placeholder="Welcome to our property! Here are our local recommendations for your stay..."
            />
            <p className="text-xs text-muted-foreground">
              Shown at the top of the guest guide. Keep it short and friendly.
            </p>
          </div>
        </div>
      </FieldGroup>
    </div>
  )
}

// ---- Step 3 ----

interface Step3Props {
  data: WizardFormData
  setData: <K extends keyof WizardFormData>(key: K, value: WizardFormData[K]) => void
  regions: WizardRegion[]
  guestBaseUrl: string
}

function Step3Setup({ data, setData, regions, guestBaseUrl }: Step3Props) {
  const regionName = data.region_id !== ""
    ? (regions.find(r => r.id === Number(data.region_id))?.name ?? "—")
    : "—"
  const urlPreview = data.slug ? `${guestBaseUrl.replace(/\/$/, "")}/${data.slug}` : null
  const wifiLabel = WIFI_INTEGRATION_TYPES.find(t => t.value === data.wifi_integration_type)?.label
    ?? data.wifi_integration_type
  const hasColor = data.primary_color && data.primary_color !== ""

  const options: Array<{
    key: keyof WizardFormData
    label: string
    description: string
    icon: React.ComponentType<{ className?: string }>
    disabled?: boolean
  }> = [
    {
      key:         "create_inside_info",
      label:       "Scaffold INSIDE info",
      description: "Creates empty categories (WiFi, Contact, Parking, etc.) ready to fill in.",
      icon:        Building2Icon,
    },
    {
      key:         "create_default_guide",
      label:       "Create default guide",
      description: "A draft property guide that guests will receive.",
      icon:        GlobeIcon,
    },
    {
      key:         "create_qr_share",
      label:       "Create QR code share",
      description: "Generates a QR code link for the default guide.",
      icon:        ExternalLinkIcon,
    },
    {
      key:         "create_wifi_share",
      label:       "Create WiFi redirect share",
      description: data.wifi_integration_type === "qr_only"
        ? "WiFi redirect not configured in Step 2 — add WiFi config first."
        : "Generates a WiFi captive portal redirect link.",
      icon:        WifiIcon,
      disabled:    data.wifi_integration_type === "qr_only",
    },
    {
      key:         "send_welcome_email",
      label:       "Send welcome email to owner",
      description: "Sends the owner their login link and guide access. Uncheck for test onboardings.",
      icon:        MailIcon,
    },
  ]

  const checkedCount = options.filter(({ key, disabled }) => !disabled && (data[key] as boolean)).length

  return (
    <div className="space-y-5">
      {/* Summary of entered data — review before creating */}
      <FieldGroup label="Review before creating" icon={ClipboardListIcon}>
        <div className="rounded-lg border divide-y text-sm">
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-xs text-muted-foreground shrink-0 w-20">Property</span>
            <span className="text-xs font-medium text-right truncate">
              {data.name || "—"}{data.property_type ? ` — ${data.property_type}` : ""}
            </span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-xs text-muted-foreground shrink-0 w-20">Owner</span>
            <span className="text-xs font-medium text-right truncate">{data.owner_email || "—"}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-xs text-muted-foreground shrink-0 w-20">Region</span>
            <span className="text-xs font-medium text-right">{regionName}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-xs text-muted-foreground shrink-0 w-20">URL</span>
            {urlPreview ? (
              <a
                href={urlPreview}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono hover:underline text-muted-foreground truncate max-w-[200px]"
              >
                {urlPreview}
              </a>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </div>
          <div className="flex items-center justify-between px-4 py-2.5">
            <span className="text-xs text-muted-foreground shrink-0 w-20">WiFi</span>
            <span className="text-xs font-medium text-right">{wifiLabel}</span>
          </div>
          {hasColor && (
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-muted-foreground shrink-0 w-20">Brand color</span>
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-4 w-4 rounded border"
                  style={{ backgroundColor: data.primary_color }}
                  aria-hidden="true"
                />
                <span className="text-xs font-mono">{data.primary_color}</span>
              </div>
            </div>
          )}
          {data.logo_image && (
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-muted-foreground shrink-0 w-20">Logo</span>
              <img
                src={URL.createObjectURL(data.logo_image)}
                alt="Logo preview"
                className="h-8 rounded border object-contain"
              />
            </div>
          )}
        </div>
      </FieldGroup>

      <Separator />

      {/* Auto-create options */}
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-900 dark:bg-blue-950/30">
        <SparklesIcon className="mt-0.5 size-4 shrink-0 text-blue-600 dark:text-blue-400" />
        <div>
          <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
            What gets created
          </p>
          <p className="mt-0.5 text-xs text-blue-700 dark:text-blue-300">
            Select what to auto-create when the property is saved. Everything here can be added or changed later.
          </p>
        </div>
      </div>

      <div className="space-y-2" role="group" aria-label="What gets created">
        {options.map(({ key, label, description, icon: Icon, disabled }) => {
          const checked = data[key] as boolean
          const checkboxId = `step3-${key}`
          return (
            <label
              key={key}
              htmlFor={checkboxId}
              className={[
                "flex items-start gap-3 rounded-lg border p-4 transition-colors",
                disabled
                  ? "cursor-not-allowed opacity-50 border-border"
                  : checked
                    ? "cursor-pointer border-primary/30 bg-primary/5"
                    : "cursor-pointer border-border hover:bg-accent/50",
              ].join(" ")}
            >
              <Checkbox
                id={checkboxId}
                checked={!disabled && checked}
                disabled={disabled}
                onCheckedChange={(v) =>
                  setData(key, (v === true) as WizardFormData[typeof key])
                }
                className="mt-0.5"
                aria-describedby={`${checkboxId}-desc`}
              />
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-tight">{label}</p>
                  <p id={`${checkboxId}-desc`} className="mt-0.5 text-xs text-muted-foreground">
                    {description}
                  </p>
                </div>
              </div>
            </label>
          )
        })}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        {checkedCount} of {options.filter(o => !o.disabled).length} options selected
      </p>
    </div>
  )
}

// ---- Completion screen ----

function CompletionScreen({ property, urls, guestBaseUrl }: { property: OnboardCompletedProperty; urls: CompletionUrls; guestBaseUrl: string }) {
  const [copied, setCopied] = useState<string | null>(null)

  const progress = checklistProgress(property.checklist)
  const guestUrl = `${guestBaseUrl}/${property.slug}`

  // The shared checklist component is decoupled from any specific urls shape;
  // build the per-item link map here (matches the dashboard's mapping).
  const checklistLinks: ChecklistLinks = {
    logo_added:            urls.settings_branding ?? null,
    wifi_configured:       urls.settings_wifi ?? null,
    inside_info_published: urls.inside ?? null,
    default_guide_active:  urls.guides ?? null,
    qr_generated:          urls.delivery ?? null,
    pois_count_ok:         urls.pois ?? null,
    first_guest_session:   null,
  }

  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(label)
        setTimeout(() => setCopied(null), 2000)
      })
      .catch(() => {
        /* clipboard unavailable (insecure context / denied) — fail quietly */
      })
  }, [])

  const navigate = (url?: string) => {
    if (url) router.visit(url)
  }

  return (
    <div className="space-y-6">
      {/* sr-only copy confirmation for assistive tech */}
      <span aria-live="polite" className="sr-only">
        {copied ? "Copied to clipboard" : ""}
      </span>

      {/* Success header — celebrate creation; reserve "ready" for 7/7 */}
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/50">
          <CheckCircle2Icon className="size-8 text-green-600 dark:text-green-400" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {property.name} {progress.allComplete ? "is ready to go live" : "is set up"}
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {progress.allComplete
            ? "All setup tasks are complete."
            : "Finish these steps to go live within 48 hours"}
          {property.region_name ? ` · created in ${property.region_name}` : ""}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* Hero column — go-live checklist (the operator's actual job) */}
        <div className="md:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Next steps to go live</CardTitle>
              {progress.allComplete ? (
                <Badge variant="default" className="w-fit bg-green-500 hover:bg-green-500">
                  All setup tasks complete
                </Badge>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{progress.completedCount} of {progress.totalCount} complete</span>
                    <span>{progress.progressPct}%</span>
                  </div>
                  <ChecklistProgressBar progressPct={progress.progressPct} />
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-0">
              {progress.allComplete ? (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={() => navigate(urls.dashboard)}
                  disabled={!urls.dashboard}
                >
                  <LayoutDashboardIcon className="mr-2 size-4" />
                  Review and go live
                </Button>
              ) : (
                <div className="flex flex-col divide-y divide-border">
                  {progress.keys.map((key) => (
                    <ChecklistItem
                      key={key}
                      label={CHECKLIST_LABELS[key]}
                      done={property.checklist[key]}
                      href={checklistLinks[key]}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Support column — delivery (QR + guest URL) */}
        <div className="md:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Delivery</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {property.qr_share_token ? (
                <div className="flex flex-col items-center gap-3">
                  <div
                    className="w-full max-w-[160px] rounded-lg border bg-white p-2"
                    role="img"
                    aria-label="QR code for the guest guide"
                  >
                    <QRCode
                      value={`${guestBaseUrl}/g/${property.qr_share_token}`}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      style={{ width: "100%", height: "auto" }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Scan to open the guest guide</p>
                  {urls.download_qr_base && property.qr_share_id && (
                    <div className="flex w-full gap-2">
                      <Button variant="outline" size="sm" className="flex-1" asChild>
                        <a href={`${urls.download_qr_base}?share_id=${property.qr_share_id}&fmt=svg`}>
                          <DownloadIcon className="mr-1.5 size-3.5" />
                          Print (SVG)
                        </a>
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1" asChild>
                        <a href={`${urls.download_qr_base}?share_id=${property.qr_share_id}&fmt=png`}>
                          <DownloadIcon className="mr-1.5 size-3.5" />
                          PNG
                        </a>
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    {property.checklist.default_guide_active
                      ? "No QR code yet. Generate one to hand to guests at check-in."
                      : "No QR code yet. Activate a guide first, then create a QR in Delivery."}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() =>
                      navigate(property.checklist.default_guide_active ? urls.delivery : urls.guides)
                    }
                    disabled={property.checklist.default_guide_active ? !urls.delivery : !urls.guides}
                  >
                    {property.checklist.default_guide_active ? "Create QR code" : "Set up your guide"}
                    <ChevronRightIcon className="ml-1.5 size-3.5" />
                  </Button>
                </div>
              )}

              <Separator />

              {/* Guest URL — full, shareable */}
              <div>
                <p className="mb-1 text-xs font-medium text-muted-foreground">Guest URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs font-mono">{guestUrl}</code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => copyToClipboard(guestUrl, "url")}
                    aria-label="Copy guest URL"
                  >
                    {copied === "url" ? (
                      <CheckIcon className="size-3.5 text-green-600" />
                    ) : (
                      <ClipboardCopyIcon className="size-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Status + email details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Property details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          <div className="flex items-center justify-between py-3">
            <span className="text-sm text-muted-foreground">Onboarding status</span>
            <Badge variant="outline" className={ONBOARDING_STATUS_BADGE_CLASSES[property.onboarding_status] ?? ""}>
              {ONBOARDING_STATUS_LABELS[property.onboarding_status] ?? property.onboarding_status}
            </Badge>
          </div>
          {property.email_sent && property.owner_email && (
            <>
              <Separator />
              <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                <MailIcon className="h-4 w-4 shrink-0" />
                <span>Welcome email sent to {property.owner_email}</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Actions — both secondary; the checklist items are the primary CTAs */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
        <Button variant="outline" size="lg" onClick={() => navigate(urls.dashboard)} disabled={!urls.dashboard}>
          <Building2Icon className="mr-2 size-4" />
          Go to dashboard
        </Button>
        <Button variant="outline" size="lg" onClick={() => navigate(urls.onboard)} disabled={!urls.onboard}>
          <RocketIcon className="mr-2 size-4" />
          Onboard another
        </Button>
      </div>
    </div>
  )
}

// ---- Main page ----

export default function OnboardWizard({ regions, completed_property, urls }: Props) {
  const { errors, config } = usePage<SharedProps>().props
  const [currentStep, setCurrentStep] = useState(1)

  const { data, setData, post, processing, transform } = useForm<WizardFormData>({
    // Step 1
    name:               "",
    property_type:      "",
    owner_email:        "",
    owner_first_name:   "",
    owner_last_name:    "",
    region_id:          "",
    address:            "",
    contact_phone:      "",
    contact_email:      "",
    // Step 2
    logo_image:             null,
    cover_image:            null,
    primary_color:          "",
    welcome_message:        "",
    slug:                   "",
    wifi_integration_type:  "qr_only",
    wifi_network_name:      "",
    wifi_password:          "",
    // Step 3
    create_inside_info:   true,
    create_default_guide: true,
    create_qr_share:      true,
    create_wifi_share:    true,
    send_welcome_email:   false,
  })

  transform((d) => ({ onboard: d }))

  function step1Valid(): boolean {
    return !!(data.name && data.property_type && data.owner_email && data.region_id)
  }

  function step2Valid(): boolean {
    return !!data.slug
  }

  function handleNext() {
    setCurrentStep((s) => s + 1)
  }

  function handleBack() {
    setCurrentStep((s) => s - 1)
  }

  function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault()
    if (currentStep !== 3 || processing || !urls.submit) return
    post(urls.submit, {
      forceFormData: true,
      onError: () => {
        // Validation error came back while on step 3 — navigate to step 1 where errors render.
        // Scroll so field errors above the fold are immediately visible.
        setCurrentStep(1)
        window.scrollTo({ top: 0, behavior: "smooth" })
      },
    })
  }

  if (completed_property) {
    return (
      <div className="mx-auto max-w-4xl py-8">
        <CompletionScreen property={completed_property} urls={urls} guestBaseUrl={config.guest_base_url} />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Onboard a property</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Complete all three steps to create the property and send the owner their setup email.
        </p>
      </div>

      <StepIndicator current={currentStep} total={3} />

      {errors.base && (
        <div
          className="mb-6 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          role="alert"
        >
          <span className="font-medium">Error:</span>
          <span>{errors.base}</span>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {currentStep === 1 && "Property basics"}
            {currentStep === 2 && "Customise & delivery"}
            {currentStep === 3 && "Confirm & create"}
          </CardTitle>
          <CardDescription>
            {currentStep === 1 && "Set up the property identity, assign an owner, and specify location."}
            {currentStep === 2 && "Configure WiFi integration and customise the guest experience."}
            {currentStep === 3 && "Review your submission, choose what to auto-create, then hit Create property."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {currentStep === 1 && (
              <Step1Basics data={data} setData={setData} regions={regions} errors={errors} guestBaseUrl={config.guest_base_url} />
            )}
            {currentStep === 2 && (
              <Step2Branding data={data} setData={setData} />
            )}
            {currentStep === 3 && (
              <Step3Setup data={data} setData={setData} regions={regions} guestBaseUrl={config.guest_base_url} />
            )}

            <div className="mt-8 flex items-center justify-between border-t pt-6">
              {currentStep > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleBack}
                  disabled={processing}
                >
                  <ChevronLeftIcon className="mr-1.5 size-4" />
                  Back
                </Button>
              ) : (
                /* Spacer to keep Next/Submit right-aligned on step 1 */
                <div />
              )}

              {currentStep < 3 ? (
                <Button
                  key="nav-continue"
                  type="button"
                  onClick={handleNext}
                  disabled={
                    (currentStep === 1 && !step1Valid()) ||
                    (currentStep === 2 && !step2Valid())
                  }
                >
                  Continue
                  <ChevronRightIcon className="ml-1.5 size-4" />
                </Button>
              ) : (
                <Button key="nav-submit" type="submit" disabled={processing} size="lg">
                  {processing ? (
                    <>
                      <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <RocketIcon className="mr-2 size-4" />
                      Create property
                    </>
                  )}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

OnboardWizard.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
